import React, { useEffect, useRef, useState } from "react";
import './NextPrevComponent.css';
import { motion } from "framer-motion";
import CornerComponent from "../CornerComponent/CornerComponent";
import ProgressiveBackgroundImg from "../ProgressiveBackgroundImg/ProgressiveBackgroundImg";
import { getGutterWidth } from "../../globals/helpers";
import { primaryInput } from "detect-it";
import { useInView } from "react-intersection-observer";
import { nextPrevVideoVariants } from "./variants";


const NextPrevComponent = ({nextProject, prevProject, scroller, handleNavigate, isSecondaryView, transitionGLRef, mainBodyRef, isSafari, dynamicSrcType}) => {
    const dividerRef = useRef(null);
    const nextTileRef = useRef(null);
    const prevTileRef = useRef(null);
    const prevTitleRef = useRef(null);
    const nextTitleRef = useRef(null);
    const fullTileRef = useRef(null);
    const overallWrapperRef = useRef(null);
    const dividerWidth = 0.1;
    const [isSelected, setIsSelected] = useState(null);

    const { ref: titleIntersectRef, inView: titleInView } = useInView({
        triggerOnce: true,
        rootMargin: "0px 0px 0px 0px",
        threshold: 0,
    });

    const leftTileVariants = {
        initial: {
            transform: "scale3d(1,1,1)"
        },
        animate: {
            transform: "scale3d(1,1,1)"
        }, exit: (i) => ({
            opacity: i[1] ? 1 : 0,
            width: i[1] ? i[0] ? '100%' : null : null,
            transform: i[1] ? i[0] ? "scale3d(1,1,1)" : "scale3d(1,1,1) translate3d(-55%, 0, 0)" : "scale3d(1,1,1) translate3d(-55%, 0, 0)",
            transition: {
                duration: i[1] ? 1.25 : 0.65,
                ease: [0.76, 0, 0.24, 1]
            },
            zIndex: i[0] ? 5 : 1
        })
    }

    const overallWrapperVariants = {
        initial: {
            width: isSecondaryView ? "calc(100%)" : "calc(100%)"
        }, animate: {
            width: isSecondaryView ? "calc(100%)" : "calc(100%)"
        }, exit: (i) => ({
            width: "100%",
            transition: {
                delay: i ? isSecondaryView ? 1.3 : 0.5 : 0,
                duration: i ? 1 : 0.65,
                ease: [0.25, 1, 0.5, 1]
            }
        })
    }

    const rightTileVariants = {
        initial: {
            transform: "scale3d(1,1,1)"
        }, animate: {
            transform: "scale3d(1,1,1)"
        }, exit: (i) => ({
            opacity: i[2] ? 0 : i[1] ? 1 : 0,
            width: i[3] ? null : i[1] ? i[0] ? '100%' : null : null,
            transform: i[1] ? i[0] ? i[2] ? "scale3d(1,1,1) translate3d(-110%, 0, 0)" : "scale3d(1,1,1) translate3d(-55%, 0, 0)" : "scale3d(1,1,1) translate3d(55%, 0, 0)" :  "scale3d(1,1,1) translate3d(55%, 0, 0)",
            transition: {
                duration: i[1] ? 1.25 : 0.65,
                ease: [0.76, 0, 0.24, 1]
            },
            zIndex: i[0] ? 5 : 1
        })
    }

    const dividerVariants = {
        initial: {
            transform: "scale3d(1, 1, 1)"
        }, animate: {
            transform: "scale3d(1, 1, 1)"
        }, exit: (i) => ({
            transform: i[1] ? `scale3d(${(0.5/dividerWidth) + (10*(dividerWidth/2))+0.1},1 ,1)` : `scale3d(${(dividerWidth) + (15*(dividerWidth))+0.1},1 ,1)`,
            transformOrigin: i[1] ? i[0] ? "left" : "right" : "center",
            transition: {
                duration: i[1] ? 1 : 0.65,
                ease: [0.76, 0, 0.24, 1]
            }
        })
    }

    const npTitleVariants = {
        initial:{
            zIndex: 1,
            transform: "translate3d(0, 0%, 0)",
        }, selectedExit: {
            transform: "translate3d(0, 100%, 0)",
            zIndex: 5,
            transition: {
                duration: 1,
                ease: [0.76, 0, 0.24, 1],
            }
        }, unselectedExit: {
            transform: "translate3d(0, 100%, 0)",
            zIndex: 5,
            transition: {
                duration: 0.65,
                ease: [0.76, 0, 0.24, 1],
            }
        }
    }

    const npTitleWrapVariants = {
        initial:{

        }, selectedExit: {
            transition: {staggerChildren: 0.025}
        }, unselectedExit:(isPrev) => ({
            opacity: 0, 
            transform: `translate3d(0, 0%, 0)`,
            transition: {
                duration: 0.65,
                ease: [0.16, 1, 0.3, 1],
                opacity: {
                    ease: [0.5, 0, 0.75, 0],
                    duration: 0.65
                }
            }
        }),
        exit: {
            opacity: 0,
            transform: `translate3d(0, 0%, 0)`,
            transition: {
                duration: 0.75,
                ease: [0.76, 0, 0.24, 1],
                opacity: {
                    ease: [0.5, 0, 0.75, 0],
                    duration: 0.65
                }
            }
        }
    }

    const npSubTitleWrapVariants = {
        initial:{

        }, selectedExit: {
            transition: {staggerChildren: 0.025}
        }, unselectedExit:(isPrev) => ({
            opacity: 0, 
            transform: `translate3d(-50%, 0%, 0)`,
            transition: {
                duration: 0.65,
                ease: [0.16, 1, 0.3, 1]
            }
        }),
        exit: {
            opacity: 0,
            transform: `translate3d(-50%, 0%, 0)`,
            transition: {
                duration: 0.75,
                ease: [0.76, 0, 0.24, 1]
            }
        }
    }

    const repositionTransition = () => {
        let posInfo = overallWrapperRef.current.getBoundingClientRect();
        let yPos = posInfo.top;
        let delta = 60 - yPos
        mainBodyRef.current.style.transform = `translate3d(0%, ${delta}px, 0)`;
        transitionGLRef.current.style.transform = `translate3d(0%, ${delta}px, 0)`;
        overallWrapperRef.current.style.transform = `translate3d(-50%, ${delta}px, 0)`;
    }

    const handleClick = (isSelected) => {
        //NEEDS DYNAMIC CHANGE
        let stylingDetails = getGutterWidth(window.innerWidth);
        let targProject = isSelected == 'n' ? nextProject : prevProject;
        let heightMinusNavbar = window.innerHeight - 60;
        let sidePaddingWidth = stylingDetails.main * 2;
        let videoWidth = window.innerWidth - sidePaddingWidth;
        let calculatedVideoHeight = videoWidth * 1/targProject.aspectRatio;
        let nextHeight = (heightMinusNavbar - calculatedVideoHeight);

        try{
            prevTileRef.current.classList.add("no-transition");
        }catch{}

        try{
            nextTileRef.current.classList.add("no-transition");
        }catch{}


        try{
            scroller.stop();
            scroller.scrollTo(scroller.scroll.instance.scroll.y, { duration: 0});
        } catch{}
        setTimeout(() => {
            repositionTransition()
        }, 50)

        if(isSelected == 'p'){
            if(prevTitleRef.current)prevTitleRef.current.style.zIndex=5;
            handleNavigate('/projects/'+prevProject.title, true);
        } else{
            if(nextTitleRef.current)nextTitleRef.current.style.zIndex=5;
            handleNavigate('/projects/'+nextProject.title, true);
        }
        setIsSelected(isSelected);
    }

    const handleMouseEnter = (targRef) => {
        if(targRef.current){
            targRef.current.style.opacity = 1;
        }
        try{
            let test = document.getElementById('cursor-circle');
            test.classList.add("cursor-active")
        }
        catch{

        }
    }

    const handleMouseLeave = (targRef)  => {
        if(targRef.current){
            targRef.current.style.opacity = 0.25;
        }
        try{
            let test = document.getElementById('cursor-circle');
            test.classList.remove("cursor-active")
        }
        catch{

        }
    }

    return(
        <>
            <motion.div 
                className="np_section__wrapper"
                initial="initial"
                animate="animate"
                exit="exit"
                ref={overallWrapperRef}
                variants={overallWrapperVariants}  
                custom={isSelected}
                 
            >
                <CornerComponent 
                    size={40}
                    wrappingRoom={0}
                    displayIndex={6}
                    performInitial={false}
                />
                {
                    !isSecondaryView ? 
                        <div 
                            className="np_tiles__wrapper"
                        >
                            <motion.div
                                className={`prev_tile np_half__wrapper ${primaryInput=="touch" ? 'ts-half' : 'ms-half'}`}
                                ref={prevTileRef}
                                variants={leftTileVariants}
                                custom={[isSelected == 'p', isSelected]}
                                onClick={() => {handleClick('p')}}
                                onMouseEnter={() => handleMouseEnter(prevTileRef)}
                                onMouseLeave={() => handleMouseLeave(prevTileRef)}
                            >
                                {!isSafari ?
                                    <>
                                        <div className="tile_left"  >
                                            <video playsInline muted autoPlay loop >
                                                <source 
                                                    src={prevProject.gifStyling.backgroundImage}
                                                />
                                            </video>
                                        </div>
                                        <div className="tile_right">
                                            <video muted playsInline autoPlay loop>
                                                <source 
                                                    src={prevProject.gifStyling.backgroundImage}
                                                />
                                            </video>    
                                        </div>
                                    </>
                                    :
                                        <div className="saf_tile">
                                            <video
                                                    playsInline muted autoPlay loop 
                                                    style={{
                                                        height: "100%",
                                                        width: "auto",
                                                        objectFit: "cover",
                                                        objectPosition: "center"
                                                    }}
                                                >
                                                    <source 
                                                        src={prevProject.gifStyling.backgroundImage}
                                                    />
                                                </video>
                                        </div>
                                }
                            </motion.div>
                            <div
                                ref={prevTitleRef}
                                className="np_meta__wrapper prev_title"
                            >
                                    <motion.div 
                                        className="np_title__wrapper"
                                        initial="initial"
                                        animate={"initial"}
                                        exit={isSelected == "p" ? "selectedExit" : "unselectedExit"}
                                        variants={npTitleWrapVariants}
                                        custom={true}
                                    >
                                        {"PREV".split("").flat().map((char, idx) => (
                                            <motion.span
                                                variants={npTitleVariants}
                                            >{char}</motion.span>
                                        ))}
                                    </motion.div>
                                    {/* <motion.div 
                                        className="np_artist__wrapper"
                                        animate={"initial"}
                                        exit={isSelected == "p" ? "selectedExit" : "unselectedExit"}
                                        variants={npSubTitleWrapVariants}
                                        custom={true}
                                    >
                                        {
                                            prevProject.title.split("").flat().map((char, idx) => (
                                                char == " " ? <span>&nbsp;</span> :
                                                <motion.span
                                                    variants={npTitleVariants}
                                                >{char}</motion.span>
                                            ))
                                        }
                                    </motion.div> */}
                            </div>
                            <div className="np_diver_wrapper">
                                <motion.div
                                    className="np_diver" 
                                    ref={dividerRef}
                                    variants={dividerVariants}
                                    custom={[isSelected == 'p', isSelected]}
                                />
                            </div>
                            <motion.div 
                                className={`next_tile np_half__wrapper ${primaryInput=="touch" ? 'ts-half' : 'ms-half'}`}
                                ref={nextTileRef}
                                variants={rightTileVariants}
                                custom={[isSelected == 'n', isSelected, isSafari]}
                                onClick={() => {handleClick('n')}}
                                onMouseEnter={() => handleMouseEnter(nextTileRef)}
                                onMouseLeave={() => handleMouseLeave(nextTileRef)}
                            >
                                {
                                    !isSafari ?
                                        <>
                                            <div 
                                                className="tile_left"  
                                            >
                                                <video muted playsInline autoPlay loop>
                                                    <source src={nextProject.gifStyling.backgroundImage} />
                                                </video>
                                            </div>
                                            <div className="tile_right">
                                                <video muted playsInline autoPlay loop>
                                                    <source 
                                                        src={nextProject.gifStyling.backgroundImage}
                                                    />
                                                </video>
                                            </div>
                                        </>
                                    :
                                        <div className="saf_tile">
                                            <video
                                                playsInline muted autoPlay loop 
                                                style={{
                                                    height: "100%",
                                                    width: "auto",
                                                    objectFit: "cover",
                                                    objectPosition: "center"
                                                }}
                                            >
                                                <source 
                                                    src={nextProject.gifStyling.backgroundImage}
                                                />
                                            </video>
                                        </div>
                                }
                            </motion.div>
                            <div 
                                ref={nextTitleRef}
                                className="np_meta__wrapper next_title"
                            >
                                    <motion.div 
                                        className="np_title__wrapper"
                                        initial="initial"
                                        animate={"initial"}
                                        exit={isSelected == "n" ? "selectedExit" : "unselectedExit"}
                                        variants={npTitleWrapVariants}
                                        custom={false}    
                                    >
                                        {"next".split("").flat().map((char, idx) => (
                                            <motion.span
                                                variants={npTitleVariants}
                                            >{char}</motion.span>
                                        ))}
                                    </motion.div>
                                {/* <motion.div 
                                    className="np_artist__wrapper"
                                    initial="initial"
                                    animate={"initial"}
                                    exit={isSelected == "n" ? "selectedExit" : "unselectedExit"}
                                    variants={npSubTitleWrapVariants}
                                    custom={false}
                                >
                                    {
                                        nextProject.title.split("").flat().map((char, idx) => (
                                            char == " " ? <span>&nbsp;</span> :
                                            <motion.span
                                                variants={npTitleVariants}
                                            >{char}</motion.span>
                                        ))
                                    }
                                </motion.div> */}
                            </div>
                        </div>
                    :
                        <div 
                            className="np_tiles__wrapper"                       
                        >
                            <div 
                                ref={nextTitleRef}
                                className="np_meta__wrapper full_title"
                                style={{
                                    zIndex:2
                                }}
                            >
                                    <motion.div 
                                        className="np_title__wrapper"
                                        initial="initial"
                                        animate={isSelected == null ? "initial" : isSelected == "n" ? "selectedExit" : "unselectedExit"}
                                        exit="exit"
                                        variants={npTitleWrapVariants}
                                        custom={false}  
                                        ref={titleIntersectRef}   
                                        
                                    >
                                    {"next".split("").flat().map((char, idx) => (
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    transform: "translate3d(0, 100%, 0)",
                                                }}
                                                className={`${titleInView ? 'rawLineIn' : ''}`}
                                            >{char}</span>
                                        ))}
                                    </motion.div>
                                <motion.div 
                                    id="np_mob_artist__wrapper"
                                    className="np_mob_artist__wrapper"
                                    initial="initial"
                                    animate={isSelected == null ? "initial" : isSelected == "n" ? "selectedExit" : "unselectedExit"}
                                    exit="exit"
                                    variants={npTitleWrapVariants}
                                    custom={false}  
                                >
                                    <motion.span
                                        style={{
                                            display: "inline-block",
                                            transform: "translate3d(0, 100%, 0)",
                                        }}
                                        className={`${titleInView ? 'rawLineIn' : ''}`}
                                        variants={npTitleVariants}
                                    >{nextProject.title}</motion.span>
                                </motion.div>
                            </div>
                            <motion.div
                                className="np_full__wrapper"
                                ref={fullTileRef}
                                custom={isSelected == 'n'}
                                onClick={() => {handleClick('n')}}
                                initial={{opacity: 1}}
                                animate={{opacity: 1}}
                                exit={isSelected == null ?{opacity: 0,
                                    transition: {
                                        duration: 0.65, 
                                        ease: "easeIn"
                                    }} : {opacity: 1}
                                }
                            >
                                <motion.div 
                                    className="tile_fullw"  
                                    style={{
                                        transformOrigin: "center",
                                    }}
                                    initial="initial"
                                    animate={isSelected == null ? "initial" : isSelected == "n" ? "selectedExit" : "unselectedExit"}
                                    exit="exit"
                                    variants={nextPrevVideoVariants}
                                >
                                    <video
                                        muted 
                                        autoPlay 
                                        loop
                                        playsInline
                                        style={{
                                            height: "100%",
                                            width: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center"
                                        }}
                                    >
                                        <source src={nextProject.gifStyling.backgroundImage} type="video/mp4"  media="(min-width: 601px)"/>
                                        <source src={nextProject.gifStyling.mobileVideo} type="video/mp4"  media="(max-width: 600px)"/>
                                    </video>
                                </motion.div>
                            </motion.div>

                        </div>
                }
                <div className="p_np_transition__wrapper">
                    <ProgressiveBackgroundImg
                        placeholderSrc={isSelected == 'p' ? 
                            prevProject.thumbnails.placeholder 
                        : 
                            nextProject.thumbnails.placeholder
                        }
                        src={
                            isSelected == 'p' ? 
                                prevProject.thumbnails.desktop.replace("desktop", dynamicSrcType)
                            :
                                nextProject.thumbnails.desktop.replace("desktop", dynamicSrcType)
                        }
                        idx={0}
                        forceFullWidth={true}
                        animateIn={false}
                        startFullRes={true}
                        addClassNames={`transition_wiper ${isSelected == 'p' ? 'left_to_right' : isSelected == "n" ? 'right_to_left' : 'null'}`}
                    />  
                </div>
            </motion.div>
        </>
    )
}

export default NextPrevComponent